/**
 * Shared API Client
 * 
 * This is the base axios instance used by all feature-specific API modules.
 * It handles authentication, error handling, and base URL configuration.
 * 
 * Features should import this and create their own API methods using it.
 */

import axios from 'axios';
import { isLocalHost } from '../../utils/assetUrl';

// Dynamic API URL detection
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  // Hosted site calls its own /api so the sign-in cookie stays on that site.
  if (!isLocalHost(hostname)) {
    return `${window.location.origin}/api`;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return `${window.location.protocol}//${hostname}:5000/api`;
};

const API_BASE_URL = getApiBaseUrl();

if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_BASE_URL);
}

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let csrfToken = null;

function rememberCsrf(value) {
  if (value) csrfToken = value;
}

function readCsrfToken() {
  const match = document.cookie.match(/(?:^|; )csrf_token=([^;]*)/);
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  }
  return csrfToken;
}

function captureCsrfHeader(response) {
  const header = response && response.headers && (response.headers['x-csrf-token'] || response.headers['X-CSRF-Token']);
  rememberCsrf(header);
}

apiClient.interceptors.request.use(
  (config) => {
    const csrf = readCsrfToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Silently exchanges the refresh token for a new access/refresh pair when an
// access token expires mid-session, instead of forcing a full re-login.
// De-duped so concurrent 401s only trigger one refresh call.
let refreshPromise = null;
const AUTH_ENDPOINTS_EXEMPT_FROM_REFRESH = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/reset-password', '/auth/logout'];
// The startup session check: an expired access token is refreshed, but a
// failure is left to AuthContext (no redirect from the landing page).
const SESSION_CHECK = '/auth/me';

function endSessionAtSignIn() {
  try {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('formationDateEdits');
  } catch (error) {
    // Sign-in still continues if the browser refuses storage.
  }
  delete apiClient.defaults.headers.common['Authorization'];
  if (window.location.pathname !== '/login') {
    try {
      // A fixed value: Login maps it to its own text and never renders it.
      sessionStorage.setItem('signout_reason', 'session_ended');
    } catch (error) {
      // The sign-in page then simply shows no reason.
    }
    window.location.href = '/login';
  }
}

export function refreshFailedTransiently(refreshError) {
  const status = refreshError && refreshError.response && refreshError.response.status;
  return !status || status >= 500 || status === 429;
}

// Free-tier hosting sleeps when idle; the first request after a nap can take
// longer than the proxy in front of the API waits, and comes back as 502/504
// even though the API then completes it. One more try after a short wait
// reaches the awake API, which recognizes the lost response (same browser,
// replacement never used) and issues a fresh pair instead of a sign-out.
const REFRESH_RETRY_DELAY_MS = 3000;
// The retry is marked so the API knows the first answer was lost (this
// browser still holds the token it sent) and issues a pair instead of a 409.
const postRefresh = (retry = false) => axios
  .post(`${API_BASE_URL}/auth/refresh`, {}, {
    withCredentials: true,
    headers: { 'X-CSRF-Token': readCsrfToken() || '', ...(retry ? { 'X-Refresh-Retry': '1' } : {}) }
  });

export const refreshWithRetry = async (post = postRefresh, sleep = (ms) => new Promise((r) => setTimeout(r, ms))) => {
  try {
    return await post();
  } catch (error) {
    if (!refreshFailedTransiently(error)) throw error;
    await sleep(REFRESH_RETRY_DELAY_MS);
    return post(true);
  }
};

const attemptTokenRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = refreshWithRetry()
      .then((response) => {
        captureCsrfHeader(response);
        return response.data;
      })
      .catch((refreshError) => {
        // Another tab refreshed the same session a moment ago; the shared
        // cookies already hold the new pair, so the caller retries without one.
        const superseded = refreshError.response?.status === 409
          && refreshError.response?.data?.code === 'REFRESH_SUPERSEDED';
        if (!superseded) throw refreshError;
        return new Promise((resolve) => setTimeout(() => resolve(null), 300));
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Response interceptor - handles errors
apiClient.interceptors.response.use(
  (response) => {
    captureCsrfHeader(response);
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const isSessionCheck = !!(originalRequest.url && originalRequest.url.includes(SESSION_CHECK));
    const isRefreshExempt = AUTH_ENDPOINTS_EXEMPT_FROM_REFRESH.some(
      (path) => originalRequest.url && originalRequest.url.includes(path)
    );
    const isExemptEndpoint = isRefreshExempt || isSessionCheck;
    const code = error.response?.data?.code;
    // An older backend sends the expired text with no code.
    const isTokenExpired = error.response?.status === 401
      && (code === 'TOKEN_EXPIRED' || (!code && error.response?.data?.error === 'Token expired'));

    if (isTokenExpired && !isRefreshExempt && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await attemptTokenRefresh();
      } catch (refreshError) {
        // No answer, 5xx or 429 from /refresh (deploy, cold start, network) is
        // not a sign-out: report that error so callers treat it as transient.
        if (refreshFailedTransiently(refreshError)) return Promise.reject(refreshError);
        if (!isSessionCheck) endSessionAtSignIn();
        return Promise.reject(error);
      }
      return apiClient(originalRequest);
    }

    // A wrong password or code on a signed-in screen is the caller's to show;
    // every other 401, including one with no code, ends the session.
    const sendsToSignIn = error.response?.status === 401 && !isExemptEndpoint
      && code !== 'AUTHENTICATION_ERROR';
    if (sendsToSignIn) {
      endSessionAtSignIn();
      return Promise.reject(error);
    }

    // A location without a trialing or paid plan; Layout shows the plan prompt.
    if (error.response?.status === 402 && error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new CustomEvent('subscription-required', {
        detail: { businessId: error.response.data.business_id }
      }));
    }

    // Only log errors in development, and don't log 401/403 for unauthenticated pages
    if (process.env.NODE_ENV === 'development') {
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      const isPublicPage = window.location.pathname === '/' ||
                          window.location.pathname === '/login' ||
                          window.location.pathname === '/register';

      // Don't log auth errors on public pages (expected behavior)
      if (!(isAuthError && isPublicPage)) {
        console.error('API Error:', error.response?.status || error.code || 'no response', code || '');
      }
    }
    return Promise.reject(error);
  }
);

// Used by AuthContext to slide the session while the person is active.
export const refreshSession = () => attemptTokenRefresh();

export default apiClient;

